import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Lightbulb, Sparkles, ArrowRight, ArrowLeft, Loader2, ScrollText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import monetizationLogo from "@/assets/monetization-logo-new.png";
import nexisLogo from "@/assets/nexis-logo-new.png";
import { MonetizationStepper } from "@/components/monetization/MonetizationStepper";

interface NexisProject {
  id: string;
  title: string;
  script: string | null;
  created_at: string;
}

const ENTRY_OPTIONS = [
  {
    id: "script",
    icon: FileText,
    title: "Use Existing Script",
    description: "Import or paste a script from Nexis AI. AI will analyze the topic, audience, and monetization potential.",
    features: ["Analyzes topic & audience", "Identifies problems solved", "Scores monetization potential"],
  },
  {
    id: "idea",
    icon: Lightbulb,
    title: "I Have My Own Idea",
    description: "Start with your niche, audience, or expertise. Define your goal and let AI create products around it.",
    features: ["Define your niche", "Specify your audience", "Set your monetization goal"],
  },
  {
    id: "discover",
    icon: Sparkles,
    title: "Find Profitable Opportunities",
    description: "Let AI discover trending, high-demand product opportunities based on creator economy trends.",
    features: ["AI-driven discovery", "Market demand analysis", "Trend-based suggestions"],
  },
];

export default function MonetizationEntryType() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [nexisProjects, setNexisProjects] = useState<NexisProject[]>([]);
  const [loadingNexis, setLoadingNexis] = useState(false);
  const [selectedNexisId, setSelectedNexisId] = useState<string | null>(null);

  // Fetch nexis projects when "from_scripts" is selected
  useEffect(() => {
    if (selectedType === "from_scripts" && user) {
      fetchNexisProjects();
    }
  }, [selectedType, user]);

  const fetchNexisProjects = async () => {
    if (!user) return;
    setLoadingNexis(true);
    try {
      const { data, error } = await supabase
        .from("nexis_projects")
        .select("id, title, script, created_at")
        .eq("user_id", user.id)
        .not("script", "is", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      setNexisProjects(data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingNexis(false);
    }
  };

  const handleContinue = async () => {
    if (!selectedType) {
      toast.error("Please select an option to continue");
      return;
    }
    if (!user) {
      toast.error("Please sign in to create a product");
      return;
    }

    // For "from_scripts" mode, require a selected script
    if (selectedType === "from_scripts") {
      if (!selectedNexisId) {
        toast.error("Please select a script");
        return;
      }
      const nexisProject = nexisProjects.find(p => p.id === selectedNexisId);
      if (!nexisProject) return;

      setCreating(true);
      try {
        const { data, error } = await supabase
          .from("monetization_projects")
          .insert({
            user_id: user.id,
            title: nexisProject.title,
            entry_type: "script",
            script_input: nexisProject.script,
            last_step: "input",
          })
          .select()
          .single();

        if (error) throw error;
        navigate(`/app/monetization-ai/project/${data.id}/input`);
      } catch (error) {
        console.error(error);
        toast.error("Failed to create project");
      } finally {
        setCreating(false);
      }
      return;
    }

    setCreating(true);
    try {
      const title = selectedType === "script" 
        ? "Script-Based Product" 
        : selectedType === "idea" 
          ? "My Product Idea" 
          : "AI-Discovered Opportunity";

      const { data, error } = await supabase
        .from("monetization_projects")
        .insert({
          user_id: user.id,
          title,
          entry_type: selectedType,
          last_step: "input",
        })
        .select()
        .single();

      if (error) throw error;
      navigate(`/app/monetization-ai/project/${data.id}/input`);
    } catch (error) {
      console.error("Error creating project:", error);
      toast.error("Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-2">
          <Button variant="ghost" size="icon" onClick={() => navigate("/app/monetization-ai")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 rounded-xl overflow-hidden">
            <img src={monetizationLogo} alt="Monetization AI" className="w-full h-full object-contain" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold">Create New Product</h1>
            <p className="text-muted-foreground">Choose how you want to start</p>
          </div>
        </div>

        {/* Stepper */}
        <div className="ml-14">
          <MonetizationStepper currentStep="entry" />
        </div>

        {/* Entry Options */}
        <div className="grid gap-4 mb-6">
          {ENTRY_OPTIONS.map((option) => (
            <div
              key={option.id}
              onClick={() => { setSelectedType(option.id); setSelectedNexisId(null); }}
              className={`glass-card p-6 cursor-pointer transition-all ${
                selectedType === option.id
                  ? "border-green-500 bg-green-500/10"
                  : "border-border/50 hover:border-green-500/50"
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                  selectedType === option.id ? "bg-green-500/20" : "bg-muted"
                }`}>
                  <option.icon className={`w-7 h-7 ${selectedType === option.id ? "text-green-400" : "text-muted-foreground"}`} />
                </div>
                <div className="flex-1">
                  <h3 className={`font-display text-lg font-semibold mb-1 ${selectedType === option.id ? "text-green-400" : "text-foreground"}`}>
                    {option.title}
                  </h3>
                  <p className="text-muted-foreground mb-3">{option.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {option.features.map((feature, i) => (
                      <span key={i} className="text-xs px-2 py-1 rounded-full bg-muted/50 text-muted-foreground">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedType === option.id ? "border-green-500 bg-green-500" : "border-muted-foreground"
                }`}>
                  {selectedType === option.id && <div className="w-2 h-2 rounded-full bg-background" />}
                </div>
              </div>
            </div>
          ))}

          {/* From My Scripts option */}
          <div
            onClick={() => setSelectedType("from_scripts")}
            className={`glass-card p-6 cursor-pointer transition-all ${
              selectedType === "from_scripts"
                ? "border-purple-500 bg-purple-500/10"
                : "border-border/50 hover:border-purple-500/50"
            }`}
          >
            <div className="flex items-start gap-4">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                selectedType === "from_scripts" ? "bg-purple-500/20" : "bg-muted"
              }`}>
                <ScrollText className={`w-7 h-7 ${selectedType === "from_scripts" ? "text-purple-400" : "text-muted-foreground"}`} />
              </div>
              <div className="flex-1">
                <h3 className={`font-display text-lg font-semibold mb-1 flex items-center gap-2 ${
                  selectedType === "from_scripts" ? "text-purple-400" : "text-foreground"
                }`}>
                  From My Scripts
                  <img src={nexisLogo} alt="Nexis" className="w-4 h-4" />
                </h3>
                <p className="text-muted-foreground mb-3">
                  Use a script you've already created in Nexis AI. AI will analyze it and suggest products.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs px-2 py-1 rounded-full bg-muted/50 text-muted-foreground">Connected to Nexis AI</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-muted/50 text-muted-foreground">Auto-import script</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-muted/50 text-muted-foreground">Faster setup</span>
                </div>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                selectedType === "from_scripts" ? "border-purple-500 bg-purple-500" : "border-muted-foreground"
              }`}>
                {selectedType === "from_scripts" && <div className="w-2 h-2 rounded-full bg-background" />}
              </div>
            </div>

            {/* Script selector */}
            {selectedType === "from_scripts" && (
              <div className="mt-4 ml-[4.5rem]">
                {loadingNexis ? (
                  <div className="flex items-center gap-2 py-4 text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Loading your scripts...</span>
                  </div>
                ) : nexisProjects.length === 0 ? (
                  <div className="p-4 rounded-lg bg-muted/30 text-center">
                    <p className="text-sm text-muted-foreground mb-2">No scripts found in Nexis AI</p>
                    <Button variant="outline" size="sm" onClick={() => navigate("/app/nexis-ai")}>
                      Go to Nexis AI
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[250px] overflow-y-auto rounded-lg">
                    {nexisProjects.map((np) => (
                      <div
                        key={np.id}
                        onClick={(e) => { e.stopPropagation(); setSelectedNexisId(np.id); }}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedNexisId === np.id
                            ? "border-purple-500 bg-purple-500/10"
                            : "border-border/30 hover:border-purple-500/40"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm text-foreground">{np.title}</p>
                          <span className="text-[10px] text-muted-foreground">
                            {new Date(np.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                          {np.script?.slice(0, 100)}...
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Continue Button */}
        <div className="flex justify-end">
          <Button
            variant="hero"
            size="lg"
            disabled={!selectedType || creating || (selectedType === "from_scripts" && !selectedNexisId)}
            onClick={handleContinue}
          >
            {creating ? "Creating..." : "Continue"}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
